/**
 * Gestion du store chargeant les thèmes
 */

import { ref, computed } from 'vue';
/***/
import axios from '@js/tools/axios';
import { defineStore } from 'pinia';

export const useThemeStore = defineStore('theme', (() => {

    // State de la liste des thèmes (total, themes)
    const themeList = ref({});

    // State pour différencier les données de la liste des thèmes
    const themes = ref([]);

    /**
     * Retourne la liste des thèmes
     * @return object
     */
    const getThemeList = computed(() => themeList.value || {});

    /**
     * Retourne le thème dont l'identifiant est en paramètre
     * @param int themeId
     * @return object|null
     */
    const getThemeById = computed(() => {
        return ((themeId) => {
            const index = themes.value.findIndex((themeFromState) => themeFromState.id === themeId);
            if(index === -1) {
                return null;
            }
            return themes.value[index];
        });
    });

    /**
     * Chargement de la liste des thèmes
     * @param Object user Utilisation dont on récupère les thémes
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadThemeList = ((user, params) => {

        const path = `/api/users/${ user.id }/themes`;

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetThemeList();
                }

                updateThemeList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Suppression du thème en paramètre
     * @param object user Utilisateur dont on supprime le thème
     * @param object theme
     * @return Promise
     */
    const removeTheme = ((user, theme) => {
        const path = `/api/users/${ user.id }/themes/${ theme.id }`;
        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemoveTheme(theme);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Suppression du thème du store
     * @param object theme
     * @return void
     */
    const onRemoveTheme = ((theme) => {

        // Suppression des thèmes déjà chargés
        const indexFromLoading = themes.value.findIndex((current) => current.id === theme.id);
        if(indexFromLoading !== -1) {
            themes.value.splice(indexFromLoading, 1);
        }

        // Vérifie que la liste a été chargée
        const currentDataList = getThemeList.value;
        if(currentDataList.total === undefined) {
            return;
        }

        // Suppression de la liste
        const newThemeList = currentDataList.themes;
        const indexFromList = newThemeList.findIndex((current) => current.id === theme.id);
        if(indexFromList !== -1) {
            newThemeList.splice(indexFromList, 1);
            themeList.value = {
                total: themeList.value.total - 1,
                themes: newThemeList
            };
        }

    });

    /**
     * Modifie un thème dans le store
     * @param object theme Données du thème à mettre à jour dans la liste
     * @return Promise
     */
    const updateTheme = ((theme) => {

        const currentThemes = themes.value;

        const index = currentThemes.findIndex((themeFromState) => themeFromState.id === theme.id);
        if(index !== -1) {
            themes.value[index] = { ...themes.value[index], ...theme };
        } else {
            themes.value.push(theme);
        }

        // Synchronisation avec les éléments de la liste
        const listData = themeList.value;
        if(listData.total !== undefined) {
            const currentList = listData.themes || [];
            const indexInList = currentList.findIndex((themeFromState) => themeFromState.id === theme.id);
            if(indexInList !== -1) {
                currentList[indexInList] = { ...currentList[indexInList], ...theme };
            } else {
                themeList.value.total += 1;
                currentList.push(theme);
            }

            themeList.value.themes = [ ...currentList ];
        }

    });

    /**
     * Modifie la liste des thèmes dans le store
     * @param data Données des thèmes : total et items
     * @return void
     */
    const updateThemeList = ((data) => {

        const currentData = themeList.value;
        if(currentData.total === undefined) {
            themeList.value = {
                total: data.total,
                themes: data.items
            };

            data.items.forEach((theme) => {
                updateTheme(theme);
            });

        } else {
            themeList.value.total = data.total;

            const currentThemes = themeList.value.themes;
            const newThemes = data.items.filter((item) => ! currentThemes.find((current) => current.id === item.id));
            themeList.value.themes = [ ...currentThemes, ...newThemes ];

            newThemes.forEach((theme) => {
                updateTheme(theme);
            });
        }

    });

    /**
     * Réinitialisation de la liste des thèmes
     * @return void
     */
    const resetThemeList = (() => {
        themeList.value = {};
        themes.value = [];
    });

    return {
        getThemeList,
        loadThemeList,
        updateTheme,
        removeTheme,
        resetThemeList
    };

}));
